import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Power } from "lucide-react";
import { api } from "@/lib/api";

const cell = "w-24 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none";

function ServiceCatalog({ services, setServices }) {
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name erforderlich."); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/service-types", { name: name.trim() });
      setServices((p) => [...p, data]);
      setName("");
      toast.success(`"${data.name}" hinzugefügt – jetzt Preise je Modell unten eintragen.`);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Hinzufügen fehlgeschlagen."); }
    finally { setAdding(false); }
  };

  const toggleActive = async (svc) => {
    try {
      const { data } = await api.put(`/service-types/${svc.id}`, { active: !svc.active });
      setServices((p) => p.map((s) => (s.id === svc.id ? data : s)));
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const remove = async (svc) => {
    try {
      await api.delete(`/service-types/${svc.id}`);
      setServices((p) => p.filter((s) => s.id !== svc.id));
      toast.success(`"${svc.name}" gelöscht.`);
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const move = async (svc, direction) => {
    try {
      const { data } = await api.patch(`/service-types/${svc.id}/move`, { direction });
      setServices((p) => {
        const next = p.map((s) => (s.id === data.id ? data : s));
        return [...next].sort((a, b) => a.order - b.order);
      });
    } catch (e) { toast.error("Verschieben fehlgeschlagen."); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 mb-6">
      <p className="font-mono text-xs uppercase tracking-widest text-[#888] mb-4">Dienstleistungen verwalten</p>
      <p className="text-sm text-[#999] mb-4 max-w-2xl">
        Neben Display und Akkutausch kannst du beliebige weitere Reparaturen anbieten (z.B. Kamera, Ladebuchse, Wasserschaden).
        Jede neue Dienstleistung erscheint unten in der Preistabelle – dort stellst du Verkaufs- und Einkaufspreis pro Modell ein.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {services.map((svc, i) => (
          <div key={svc.id} data-testid={`service-type-${svc.id}`}
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${svc.active ? "border-white/20 text-white" : "border-white/10 text-[#666]"}`}>
            <button type="button" onClick={() => move(svc, "up")} disabled={i === 0} className="text-[#666] hover:text-white transition-colors disabled:opacity-20"><ArrowUp size={11} /></button>
            <button type="button" onClick={() => move(svc, "down")} disabled={i === services.length - 1} className="text-[#666] hover:text-white transition-colors disabled:opacity-20"><ArrowDown size={11} /></button>
            <span>{svc.name}</span>
            <button type="button" onClick={() => toggleActive(svc)} data-testid={`service-type-toggle-${svc.id}`} title={svc.active ? "Aktiv – für Kunden sichtbar" : "Inaktiv – ausgeblendet"}
              className={svc.active ? "text-emerald-400" : "text-[#555]"}>
              <Power size={13} />
            </button>
            <button type="button" onClick={() => remove(svc)} data-testid={`service-type-delete-${svc.id}`} className="text-[#666] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <form onSubmit={add} className="flex gap-2">
        <input data-testid="new-service-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Kamera-Reparatur"
          className="flex-1 max-w-xs bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none" />
        <button type="submit" disabled={adding} data-testid="add-service-type-btn" className="inline-flex items-center gap-2 text-sm rounded-full bg-white text-[#050505] px-4 py-2 hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
          <Plus size={14} /> {adding ? "…" : "Hinzufügen"}
        </button>
      </form>
    </div>
  );
}

export default function PricingView() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [nw, setNw] = useState({ model: "", oled_available: true, oled_price: "", oled_cost: "", lcd_price: "", lcd_cost: "" });
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/pricing"), api.get("/service-types")]);
      setRows(p.data);
      setServices(s.data);
    } catch (e) { toast.error("Preise konnten nicht geladen werden."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setField = (id, field, value) => setRows((p) => p.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  const setServicePrice = (id, key, field, value) => setRows((p) => p.map((r) => {
    if (r.id !== id) return r;
    const sp = { ...(r.service_prices || {}) };
    sp[key] = { ...(sp[key] || { sell_price: 0, cost_price: 0 }), [field]: value };
    return { ...r, service_prices: sp };
  }));
  const spVal = (r, key, field) => r.service_prices?.[key]?.[field] ?? 0;

  const save = async (row) => {
    setSavingId(row.id);
    try {
      const service_prices = {};
      services.forEach((svc) => {
        service_prices[svc.key] = {
          sell_price: Number(spVal(row, svc.key, "sell_price")) || 0,
          cost_price: Number(spVal(row, svc.key, "cost_price")) || 0,
          original_price: Number(spVal(row, svc.key, "original_price")) || 0,
        };
      });
      await api.put(`/pricing/${row.id}`, {
        oled_available: !!row.oled_available,
        oled_price: Number(row.oled_price), oled_cost: Number(row.oled_cost) || 0, oled_original_price: Number(row.oled_original_price) || 0,
        lcd_price: Number(row.lcd_price), lcd_cost: Number(row.lcd_cost) || 0, lcd_original_price: Number(row.lcd_original_price) || 0,
        service_prices,
      });
      toast.success(`${row.model} gespeichert.`);
    } catch (e) { toast.error("Speichern fehlgeschlagen."); }
    finally { setSavingId(null); }
  };

  const remove = async (row) => {
    try {
      await api.delete(`/pricing/${row.id}`);
      setRows((p) => p.filter((r) => r.id !== row.id));
      toast.success(`${row.model} gelöscht.`);
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const move = async (row, direction) => {
    try {
      await api.patch(`/pricing/${row.id}/move`, { direction });
      await load();
    } catch (e) { toast.error("Verschieben fehlgeschlagen."); }
  };

  const addDevice = async (e) => {
    e.preventDefault();
    if (!nw.model.trim()) { toast.error("Modellname erforderlich."); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/pricing", {
        model: nw.model.trim(), oled_available: nw.oled_available,
        oled_price: Number(nw.oled_price || 0), oled_cost: Number(nw.oled_cost || 0),
        lcd_price: Number(nw.lcd_price || 0), lcd_cost: Number(nw.lcd_cost || 0),
      });
      setRows((p) => [...p, data]);
      setNw({ model: "", oled_available: true, oled_price: "", oled_cost: "", lcd_price: "", lcd_cost: "" });
      toast.success("Gerät hinzugefügt – Dienstleistungspreise unten in der Tabelle ergänzen.");
    } catch (e) { toast.error(e.response?.data?.detail || "Hinzufügen fehlgeschlagen."); }
    finally { setAdding(false); }
  };

  return (
    <div data-testid="pricing-view">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3">Preise & Geräte</h1>
      <p className="text-[#999] mb-8 max-w-2xl">
        Stelle OLED- (Original), LCD- (günstiger) und Dienstleistungspreise pro Modell ein. <strong className="text-white">Einkauf</strong> ist
        dein eigener (Brutto-)Einkaufspreis für das Ersatzteil – damit du deinen Gewinn je Reparatur siehst (Details unter „Umsatzstatistik").
        <strong className="text-white"> Streichpreis ist komplett optional</strong> (0 lassen = wird nicht angezeigt) und hat nichts mit deinem
        Einkauf zu tun: Er zeigt Kunden nur einen durchgestrichenen, höheren Vergleichspreis neben dem aktuellen Verkaufspreis (reine Marketing-Optik).
        Deaktiviere OLED für Geräte, die von Haus aus ein LCD haben.
      </p>

      <ServiceCatalog services={services} setServices={setServices} />

      {/* Add device */}
      <form onSubmit={addDevice} data-testid="add-device-form" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 mb-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#888] mb-1">Neues Modell</label>
          <input data-testid="new-model" value={nw.model} onChange={(e) => setNw((s) => ({ ...s, model: e.target.value }))} placeholder="iPhone 17 Pro" className="w-44 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#ccc] pb-2">
          <input type="checkbox" data-testid="new-oled-available" checked={nw.oled_available} onChange={(e) => setNw((s) => ({ ...s, oled_available: e.target.checked }))} className="accent-white" /> OLED verfügbar
        </label>
        <div><label className="block text-[10px] font-mono uppercase tracking-widest text-[#888] mb-1">OLED € (Verkauf)</label><input data-testid="new-oled-price" type="number" value={nw.oled_price} onChange={(e) => setNw((s) => ({ ...s, oled_price: e.target.value }))} className={cell} /></div>
        <div><label className="block text-[10px] font-mono uppercase tracking-widest text-[#888] mb-1">OLED € (Einkauf)</label><input data-testid="new-oled-cost" type="number" value={nw.oled_cost} onChange={(e) => setNw((s) => ({ ...s, oled_cost: e.target.value }))} className={cell} /></div>
        <div><label className="block text-[10px] font-mono uppercase tracking-widest text-[#888] mb-1">LCD € (Verkauf)</label><input data-testid="new-lcd-price" type="number" value={nw.lcd_price} onChange={(e) => setNw((s) => ({ ...s, lcd_price: e.target.value }))} className={cell} /></div>
        <div><label className="block text-[10px] font-mono uppercase tracking-widest text-[#888] mb-1">LCD € (Einkauf)</label><input data-testid="new-lcd-cost" type="number" value={nw.lcd_cost} onChange={(e) => setNw((s) => ({ ...s, lcd_cost: e.target.value }))} className={cell} /></div>
        <button type="submit" disabled={adding} data-testid="add-device-btn" className="inline-flex items-center gap-2 text-sm rounded-full bg-white text-[#050505] px-5 py-2.5 hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
          <Plus size={15} /> {adding ? "…" : "Gerät hinzufügen"}
        </button>
      </form>

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : (
        <div className="relative">
          <p className="sm:hidden text-xs text-[#666] mb-2 flex items-center gap-1.5">
            <ArrowUp className="rotate-90" size={12} /> Tabelle nach rechts wischen für mehr Preise
          </p>
          <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0a0a0a]">
          <table className="w-full min-w-[1200px]" data-testid="pricing-table">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#888] border-b border-white/10">
                <th className="p-4 sticky left-0 z-20 bg-[#0a0a0a] border-r border-white/10">Modell & Aktionen</th>
                <th className="p-4">OLED verfügbar</th>
                <th className="p-4" title="Verkauf = Preis für den Kunden. Streichpreis = optionaler durchgestrichener Vergleichspreis (nur Marketing). Einkauf = dein Brutto-Einkaufspreis für das Teil.">OLED: Verkauf / Streichpreis (optional) / Einkauf (brutto)</th>
                <th className="p-4" title="Verkauf = Preis für den Kunden. Streichpreis = optionaler durchgestrichener Vergleichspreis (nur Marketing). Einkauf = dein Brutto-Einkaufspreis für das Teil.">LCD: Verkauf / Streichpreis (optional) / Einkauf (brutto)</th>
                {services.map((svc) => <th key={svc.id} className="p-4 whitespace-nowrap" title="Verkauf = Preis für den Kunden. Streichpreis = optionaler durchgestrichener Vergleichspreis (nur Marketing). Einkauf = dein Brutto-Einkaufspreis für das Teil.">{svc.name}: Verkauf / Streichpreis (optional) / Einkauf (brutto)</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`price-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 sticky left-0 z-10 bg-[#0a0a0a] border-r border-white/10 align-top">
                    <div className="flex items-center gap-2 mb-3 whitespace-nowrap">
                      <div className="flex flex-col shrink-0">
                        <button type="button" onClick={() => move(r, "up")} data-testid={`move-up-${r.id}`} className="text-[#666] hover:text-white transition-colors"><ArrowUp size={13} /></button>
                        <button type="button" onClick={() => move(r, "down")} data-testid={`move-down-${r.id}`} className="text-[#666] hover:text-white transition-colors"><ArrowDown size={13} /></button>
                      </div>
                      <span className="font-medium">{r.model}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => save(r)} disabled={savingId === r.id} data-testid={`save-price-${r.id}`} className="inline-flex items-center gap-2 text-sm rounded-full bg-white text-[#050505] px-4 py-2 hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
                        <Save size={14} /> {savingId === r.id ? "…" : "Speichern"}
                      </button>
                      <button onClick={() => remove(r)} data-testid={`delete-price-${r.id}`} className="w-9 h-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </td>
                  <td className="p-4">
                    <input type="checkbox" data-testid={`oled-avail-${r.id}`} checked={r.oled_available !== false} onChange={(e) => setField(r.id, "oled_available", e.target.checked)} className="accent-white w-4 h-4" />
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <input data-testid={`oled-price-${r.id}`} type="number" value={r.oled_price} onChange={(e) => setField(r.id, "oled_price", e.target.value)} className={cell} placeholder="Verkauf" title="Verkaufspreis für den Kunden" />
                      <input data-testid={`oled-original-${r.id}`} type="number" value={r.oled_original_price ?? 0} onChange={(e) => setField(r.id, "oled_original_price", e.target.value)} className={`${cell} border-dotted text-[#999]`} placeholder="Streichpreis (optional)" title="Optional: durchgestrichener Vergleichspreis, nur Marketing. 0 = nicht anzeigen." />
                      <input data-testid={`oled-cost-${r.id}`} type="number" value={r.oled_cost ?? 0} onChange={(e) => setField(r.id, "oled_cost", e.target.value)} className={`${cell} border-dashed`} placeholder="Einkauf (brutto)" title="Dein Brutto-Einkaufspreis für das Ersatzteil" />
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <input data-testid={`lcd-price-${r.id}`} type="number" value={r.lcd_price} onChange={(e) => setField(r.id, "lcd_price", e.target.value)} className={cell} placeholder="Verkauf" title="Verkaufspreis für den Kunden" />
                      <input data-testid={`lcd-original-${r.id}`} type="number" value={r.lcd_original_price ?? 0} onChange={(e) => setField(r.id, "lcd_original_price", e.target.value)} className={`${cell} border-dotted text-[#999]`} placeholder="Streichpreis (optional)" title="Optional: durchgestrichener Vergleichspreis, nur Marketing. 0 = nicht anzeigen." />
                      <input data-testid={`lcd-cost-${r.id}`} type="number" value={r.lcd_cost ?? 0} onChange={(e) => setField(r.id, "lcd_cost", e.target.value)} className={`${cell} border-dashed`} placeholder="Einkauf (brutto)" title="Dein Brutto-Einkaufspreis für das Ersatzteil" />
                    </div>
                  </td>
                  {services.map((svc) => (
                    <td key={svc.id} className="p-4">
                      <div className="flex gap-2">
                        <input data-testid={`svc-price-${svc.key}-${r.id}`} type="number" value={spVal(r, svc.key, "sell_price")} onChange={(e) => setServicePrice(r.id, svc.key, "sell_price", e.target.value)} className={cell} placeholder="Verkauf" title="Verkaufspreis für den Kunden" />
                        <input data-testid={`svc-original-${svc.key}-${r.id}`} type="number" value={spVal(r, svc.key, "original_price")} onChange={(e) => setServicePrice(r.id, svc.key, "original_price", e.target.value)} className={`${cell} border-dotted text-[#999]`} placeholder="Streichpreis (optional)" title="Optional: durchgestrichener Vergleichspreis, nur Marketing. 0 = nicht anzeigen." />
                        <input data-testid={`svc-cost-${svc.key}-${r.id}`} type="number" value={spVal(r, svc.key, "cost_price")} onChange={(e) => setServicePrice(r.id, svc.key, "cost_price", e.target.value)} className={`${cell} border-dashed`} placeholder="Einkauf (brutto)" title="Dein Brutto-Einkaufspreis für das Ersatzteil" />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
